import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useSetShippingRate } from "@babackapp/return-widget-components/hooks/use-set-shipping-rate";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { useShippingRates } from "@babackapp/return-widget-components/queries/use-shipping-rates";
import type { FinancialSummary } from "@babackapp/return-widget-components/types";
import type { ShippingRateDto } from "@babackapp/private-api-client/generated/requests/types.gen";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useWidgetParams } from "./use-widget-params";

export type UseCheckoutReturn = {
  financialSummary: FinancialSummary;
  shippingRates: ShippingRateDto[];
  selectedRateId: string | null;
  selectRate: (rateId: string) => void;
  canProceed: boolean;
  onProceed: () => void;
  isLoadingRates: boolean;
  ratesError: Error | null;
  checkoutToken: string | null;
};

export function useCheckout(): UseCheckoutReturn {
  const { orderId } = useWidgetParams();
  const draft = useDraftReturn(orderId);
  const { data: order } = usePublicOrder(orderId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);
  const setShippingRate = useSetShippingRate(orderId);
  const navigate = useNavigate();

  const shippingRatesMutation = useShippingRates();
  const { mutate: fetchShippingRates, status: ratesStatus } =
    shippingRatesMutation;

  const needsPayment = financialSummary.amountDue > 102;

  useEffect(() => {
    if (needsPayment && draft && ratesStatus === "idle") {
      fetchShippingRates(draft);
    }
  }, [needsPayment, draft, ratesStatus, fetchShippingRates]);

  const shippingRates: ShippingRateDto[] =
    shippingRatesMutation.data?.shipping_rates ?? [];

  const selectRate = useCallback(
    (rateId: string) => {
      const rate = shippingRates.find((r) => r.handle === rateId);
      const amountCents = rate?.price.amount
        ? Math.round(parseFloat(rate.price.amount) * 100)
        : null;
      setShippingRate(rateId, rate?.title ?? null, amountCents);
    },
    [setShippingRate, shippingRates],
  );

  const onProceed = useCallback(() => {
    navigate("../complete");
  }, [navigate]);

  const selectedRateId = draft?.shippingRateId ?? null;

  const canProceed = needsPayment ? selectedRateId !== null : true;

  return {
    financialSummary,
    shippingRates,
    selectedRateId,
    selectRate,
    canProceed,
    onProceed,
    isLoadingRates: shippingRatesMutation.status === "pending",
    ratesError: shippingRatesMutation.error,
    checkoutToken: shippingRatesMutation.data?.checkout_token ?? null,
  };
}
